import { useState, useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "convex/_generated/api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeftIcon, CheckIcon } from "@phosphor-icons/react";
import { Cpu, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/primitives";
import {
  ByomConfigForm,
  type ByomConfigFormState,
} from "@/components/settings/ai-provider";

type ProviderType = "builtin_openai" | "byom_openai_compatible";

interface TestResult {
  success: boolean;
  error?: string;
  latencyMs: number;
}

export interface AIProviderChoice {
  provider: ProviderType;
  byomConfig?: ByomConfigFormState;
}

interface AIProviderStepProps {
  onNext: (choice: AIProviderChoice) => void;
  onBack: () => void;
}

const ease = [0.25, 0.46, 0.45, 0.94] as const;

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease,
    },
  }),
};

export function AIProviderStep({ onNext, onBack }: AIProviderStepProps) {
  const testAction = useAction(
    api.workspace_ai_provider_actions.testConnectionDirect,
  );

  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(
    null,
  );
  const [byomFormConfig, setByomFormConfig] =
    useState<ByomConfigFormState | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const canContinue =
    selectedProvider === "builtin_openai" ||
    (selectedProvider === "byom_openai_compatible" &&
      testResult !== null &&
      testResult.success);

  const handleSelectBuiltIn = useCallback(() => {
    setSelectedProvider("builtin_openai");
    setByomFormConfig(null);
    setTestResult(null);
  }, []);

  const handleSelectByom = useCallback(() => {
    setSelectedProvider("byom_openai_compatible");
  }, []);

  const handleByomConfigChange = useCallback((config: ByomConfigFormState) => {
    setByomFormConfig(config);
    setTestResult(null);
  }, []);

  const handleTestConnection = useCallback(
    async (config: ByomConfigFormState) => {
      setIsTesting(true);
      setTestResult(null);
      try {
        const result = await testAction({
          endpointUrl: config.endpointUrl,
          modelName: config.modelName,
          apiKey: config.apiKey || undefined,
        });
        setTestResult(result);
      } catch {
        setTestResult({
          success: false,
          error: "Failed to test connection",
          latencyMs: 0,
        });
      } finally {
        setIsTesting(false);
      }
    },
    [testAction],
  );

  const handleContinue = useCallback(() => {
    if (!selectedProvider) return;

    if (selectedProvider === "builtin_openai") {
      onNext({ provider: "builtin_openai" });
    } else {
      onNext({
        provider: "byom_openai_compatible",
        byomConfig: byomFormConfig ?? undefined,
      });
    }
  }, [selectedProvider, byomFormConfig, onNext]);

  return (
    <div className="flex flex-col">
      {/* Back */}
      <motion.button
        type="button"
        onClick={onBack}
        custom={0}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex items-center gap-1.5 text-[13px] text-secondary hover:text-primary transition-colors mb-6 self-start"
      >
        <ArrowLeftIcon className="size-3.5" />
        Back
      </motion.button>

      <motion.div custom={1} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          How should AI features work?
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          Choose how AI-powered features like document analysis and checklist
          generation are handled in your workspace.
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-3">
        {/* Card 1: Built-in AI */}
        <motion.button
          type="button"
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={handleSelectBuiltIn}
          className={`
            w-full text-left rounded-xl border p-4 transition-all duration-200 cursor-pointer
            ${
              selectedProvider === "builtin_openai"
                ? "border-accent bg-accent/8 shadow-sm"
                : "border-border bg-white hover:bg-card-hover hover:border-border"
            }
          `}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${
                selectedProvider === "builtin_openai"
                  ? "bg-accent/15 text-accent"
                  : "bg-muted text-secondary"
              }`}
            >
              <Sparkles className="size-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`text-[14px] font-semibold ${
                    selectedProvider === "builtin_openai"
                      ? "text-accent"
                      : "text-primary"
                  }`}
                >
                  Use Built-in AI
                </p>
                {selectedProvider === "builtin_openai" && (
                  <CheckIcon className="size-4 text-accent" />
                )}
              </div>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                Powered by OpenAI. No setup required.
              </p>
            </div>
          </div>
        </motion.button>

        {/* Card 2: Connect your own model */}
        <motion.button
          type="button"
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={handleSelectByom}
          className={`
            w-full text-left rounded-xl border p-4 transition-all duration-200 cursor-pointer
            ${
              selectedProvider === "byom_openai_compatible"
                ? "border-accent bg-accent/8 shadow-sm"
                : "border-border bg-white hover:bg-card-hover hover:border-border"
            }
          `}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${
                selectedProvider === "byom_openai_compatible"
                  ? "bg-accent/15 text-accent"
                  : "bg-muted text-secondary"
              }`}
            >
              <Cpu className="size-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`text-[14px] font-semibold ${
                    selectedProvider === "byom_openai_compatible"
                      ? "text-accent"
                      : "text-primary"
                  }`}
                >
                  Connect your own model
                </p>
                {selectedProvider === "byom_openai_compatible" && (
                  <CheckIcon className="size-4 text-accent" />
                )}
              </div>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                Use Ollama, vLLM, LM Studio, or any OpenAI-compatible endpoint.
              </p>
            </div>
          </div>
        </motion.button>

        {/* BYOM Config Form (expandable) */}
        <AnimatePresence>
          {selectedProvider === "byom_openai_compatible" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease }}
              className="overflow-hidden"
            >
              <div className="rounded-xl border border-border bg-white p-5 mt-1">
                <ByomConfigForm
                  onTestResult={setTestResult}
                  onChange={handleByomConfigChange}
                  onTestConnection={handleTestConnection}
                  testResult={testResult}
                  isTesting={isTesting}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Continue */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="mt-2"
        >
          <Button
            type="button"
            variant="primary"
            disabled={!canContinue}
            onClick={handleContinue}
            className="w-full"
          >
            Continue
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
